import { useEffect, useState, type FormEvent } from "react";
import { api, ErrorApi, type Sesion } from "../api.ts";
import { Logo, CampoClave } from "./comun.tsx";
import { BotonInstalar } from "../componentes/Instalar.tsx";
import { cargarMarca, hayClinicaRecordada, marcaGuardada, urlLogoMarca, type Marca } from "../clinica.ts";
import { BannerClinica } from "../componentes/BannerClinica.tsx";

export function Login({ demo, alEntrar, alCrear }: { demo: boolean; alEntrar: (s: Sesion) => void; alCrear?: () => void }) {
  const [email, setEmail] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  // verificación en dos pasos: tras la contraseña, el código de la app (o uno de rescate)
  const [paso2, setPaso2] = useState<{ desafio: string; rescate: boolean } | null>(null);
  const [codigo, setCodigo] = useState("");
  const [usarRescate, setUsarRescate] = useState(false);
  const [confiar, setConfiar] = useState(true);
  const claveEquipo = () => "donna.eq." + email.trim().toLowerCase();
  const equipoDeConfianza = () => { try { return localStorage.getItem(claveEquipo()) ?? undefined; } catch { return undefined; } };
  // la marca de la clínica (logo y nombre); mientras carga o si no hay, se muestra la de Donna
  // se parte de la última marca recordada en este navegador (sin parpadeo) y se actualiza en segundo plano
  const [guardada] = useState(() => marcaGuardada());
  const [marca, setMarca] = useState<Marca | null | undefined>(guardada ?? undefined);
  const [logoRoto, setLogoRoto] = useState(false);
  useEffect(() => { let vivo = true; cargarMarca().then((m) => { if (vivo) setMarca(m); }); return () => { vivo = false; }; }, []);
  const logo = marca && !logoRoto ? (guardada && guardada.id === marca.id && guardada.logo === marca.logo && guardada.logoDatos) || urlLogoMarca(marca) : null;
  // si se sabe de qué clínica es, se reserva el lugar del banner mientras llega (en vez de mostrar «Donna» y cambiarlo)
  const esperando = marca === undefined && hayClinicaRecordada();
  // con el banner de la clínica activo, el banner reemplaza al encabezado «Donna» y al nombre de la clínica
  const conBanner = !!marca?.banner?.activo;

  async function entrar(e: FormEvent) {
    e.preventDefault();
    setError(""); setEnviando(true);
    try {
      if (paso2) {
        const r = await api<Sesion & { dispositivo?: string }>("POST", "/api/auth/2fa", { desafio: paso2.desafio, ...(usarRescate ? { rescate: codigo } : { codigo }), confiar });
        if (r.dispositivo) { try { localStorage.setItem(claveEquipo(), r.dispositivo); } catch { /* sin almacenamiento: pedirá el código la próxima vez */ } }
        alEntrar({ token: r.token, usuario: r.usuario });
      } else {
        const r = await api<Sesion | { requiere2fa: true; desafio: string; rescate: boolean }>("POST", "/api/auth/login", { email, contrasena, dispositivo: equipoDeConfianza() });
        if ("requiere2fa" in r) { setPaso2({ desafio: r.desafio, rescate: r.rescate }); setCodigo(""); setUsarRescate(false); } else alEntrar(r);
      }
    }
    catch (err) {
      const m = err instanceof ErrorApi ? err.message : "No se pudo iniciar sesión.";
      if (paso2 && /venció/.test(m)) setPaso2(null);
      setError(m);
    }
    finally { setEnviando(false); }
  }

  return (
    <main className="login">
      {esperando && <div className="banner-clinica banner-esqueleto" aria-hidden="true" />}
      {marca && conBanner && <BannerClinica nombre={marca.nombre} logo={logo} banner={marca.banner} alCargarLogoRoto={() => setLogoRoto(true)} />}
      <form className="card" onSubmit={entrar} aria-labelledby="t-login">
        <div className="login-cabecera">
          {!conBanner && !esperando && <div className="brand" style={{ color: "var(--accent)" }}><Logo /><span style={{ color: "var(--ink)" }}>Donna</span></div>}
          {marca && !conBanner && (
            <div className="login-marca" aria-label={`Clínica ${marca.nombre}`}>
              {logo && <img src={logo} alt={`Logo de ${marca.nombre}`} onError={() => setLogoRoto(true)} />}
              <span className="login-clinica">{marca.nombre}</span>
            </div>
          )}
        </div>
        <h1 id="t-login" style={{ fontSize: 22 }}>Inicia sesión</h1>
        {paso2 ? (
          <>
            <p className="muted" style={{ margin: 0 }}>{usarRescate ? "Escribe uno de tus códigos de rescate (sirve una sola vez)." : "Abre tu app de verificación y escribe el número de 6 dígitos."}</p>
            <label className="campo">{usarRescate ? "Código de rescate" : "Código de 6 dígitos"}
              <input className="entrada mono" autoFocus required autoComplete="one-time-code" inputMode={usarRescate ? "text" : "numeric"} maxLength={usarRescate ? 14 : 7} placeholder={usarRescate ? "XXXXX-XXXXX" : "123 456"} value={codigo} onChange={(e) => setCodigo(e.target.value)} style={{ letterSpacing: 2 }} />
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" checked={confiar} onChange={(e) => setConfiar(e.target.checked)} /> Confiar en este equipo por 30 días</label>
            {error && <div className="aviso error" role="alert">{error}</div>}
            <button className="btn pri" disabled={enviando || !codigo.trim()}>{enviando ? "Comprobando…" : "Entrar"}</button>
            {paso2.rescate && <button type="button" className="btn" onClick={() => { setUsarRescate(!usarRescate); setCodigo(""); setError(""); }}>{usarRescate ? "Usar el código de mi app" : "Perdí mi celular: usar un código de rescate"}</button>}
            <button type="button" className="btn" onClick={() => { setPaso2(null); setCodigo(""); setError(""); setContrasena(""); }}>Volver</button>
          </>
        ) : (
          <>
            <label className="campo">Correo<input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
            <CampoClave etiqueta="Contraseña" className="" autoComplete="current-password" required value={contrasena} onChange={setContrasena} />
            {error && <div className="aviso error" role="alert">{error}</div>}
            <button className="btn pri" disabled={enviando}>{enviando ? "Entrando…" : "Entrar"}</button>
            {alCrear && <button type="button" className="btn" onClick={alCrear}>Crear cuenta (clínica o laboratorio)</button>}
          </>
        )}
        {demo && (
          <div className="aviso info">
            Modo demostración. Contraseña para todos: <span className="mono">Donna2026demo</span>
            <div className="fila" style={{ marginTop: 6 }}>
              {["odontologa@demo.ec", "recepcion@demo.ec", "asistente@demo.ec", "admin@demo.ec"].map((c) => (
                <button type="button" key={c} className="btn chico" onClick={() => { setEmail(c); setContrasena("Donna2026demo"); }}>{c.split("@")[0]}</button>
              ))}
            </div>
          </div>
        )}
      </form>
      <BotonInstalar variante="login" />
    </main>
  );
}
